import React from 'react';
import styled from 'styled-components';
import Collapse from '@material-ui/core/Collapse';
import isEmpty from 'lodash/isEmpty';
import { useSolvSelector } from '~/reducers';
import Loading from '../../../../../SolvPatternLibrary/Loading';
import InsuranceUserErrorBanner from './InsuranceUserErrorBanner';
import useSelectInsuranceProfile from '../../../../hooks/useSelectInsuranceProfile';
import useSelectFetchInsuranceDetails from '../../../../hooks/useSelectFetchInsuranceDetails';
import useDetailsResult from './hooks/useDetailsResult';
import useInsuranceHeadLineCopy from './hooks/useInsuranceHeadLineCopy';
import { biggerThanOrEqualTo, smallerThan } from '~/core/util/styledComponents';
import { fontFamily, fontSize } from '~/constants/text';
import { colors } from '~/constants/colors';

const Root = styled.div.withConfig({ componentId: 'sc-13c9go7-0' })`
  display: flex;
  box-sizing: border-box;

  ${biggerThanOrEqualTo.md`
    place-self: flex-start;
  `};
`;

const HeadlineContainer = styled.div.withConfig({ componentId: 'sc-13c9go7-1' })`
  box-sizing: border-box;

  ${biggerThanOrEqualTo.sm`
    max-width: 1200px;
  `};

  ${biggerThanOrEqualTo.md`
    max-width: 400px;
  `};
`;

const LoadingContainer = styled(HeadlineContainer).withConfig({ componentId: 'sc-13c9go7-2' })`
  min-height: 265px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  margin-right: auto;

  ${smallerThan.md`
    min-height: 199px;
  `}
`;

const Title = styled.div.withConfig({ componentId: 'sc-13c9go7-3' })`
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.medium};
  color: ${colors.purpletrator};
  line-height: 24px;
  letter-spacing: 0.26px;
`;

const Subtext = styled.div.withConfig({ componentId: 'sc-13c9go7-4' })`
  font-family: ${fontFamily.book};
  color: ${colors.purpletrator};
  font-size: ${fontSize.mediumSmall};
  letter-spacing: 0.07px;
  line-height: 24px;
  text-align: left;
  margin-left: 20px;
`;

const HeaderContainer = styled.div.withConfig({ componentId: 'sc-13c9go7-5' })`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding-bottom: 12px;
`;

const UpdatingInsuranceContainer = styled.div.withConfig({ componentId: 'sc-13c9go7-6' })`
  background-color: ${colors.white};
  align-items: center;
  padding: 20px;
  display: flex;
  justify-content: space-evenly;
  font-size: ${fontSize.medium};
  font-family: ${fontFamily.medium};
  color: ${colors.squid};
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.04), 0 0 8px 0 rgba(0, 0, 0, 0.1);
  max-width: 375px;
  margin: 0 auto 12px;

  @media (min-width: 415px) {
    border-radius: 8px;
  }

  ${biggerThanOrEqualTo.sm`
    margin-top: 12px;
  `}
`;

type Props = {
  userProfileId: string;
};

const InsuranceHeadline = (props: Props) => {
  const { userProfileId } = props;
  const insuranceProfile = useSelectInsuranceProfile(userProfileId);
  const insuranceProfileError = insuranceProfile?.eligibility_reject_reason_type;
  const insuranceDetails = useSelectFetchInsuranceDetails(userProfileId);
  const detailsResults = useDetailsResult(insuranceProfile, insuranceDetails);
  const headline = useInsuranceHeadLineCopy(detailsResults);
  const submitting = useSolvSelector((state: any) => state.npi.submitting);
  const npiError = useSolvSelector((state: any) => state.npi.error);
  const insuranceDetailsError = isEmpty(insuranceDetails) && npiError;

  const areDetailsLoaded = !isEmpty(insuranceDetails);

  const getLoadingAndErrorDisplays = () => {
    if (insuranceProfileError) {
      return (
        <InsuranceUserErrorBanner
          insuranceProfile={insuranceProfile}
          userProfileId={userProfileId}
        />
      );
    }
    if (insuranceDetailsError) {
      return null;
    }
    return <Loading />;
  };

  if (areDetailsLoaded && !headline) {
    return null;
  }

  return (
    <Root>
      <Collapse appear in={submitting} mountOnEnter timeout="auto" unmountOnExit>
        <UpdatingInsuranceContainer>
          Updating Insurance... <Loading size={30} />
        </UpdatingInsuranceContainer>
      </Collapse>

      {areDetailsLoaded ? (
        <>
          {headline && (
            <HeadlineContainer>
              <HeaderContainer>
                <img height={49} src={headline.icon} />
                <Title>{headline.title}</Title>
              </HeaderContainer>

              <Subtext>{headline.subtext}</Subtext>
            </HeadlineContainer>
          )}
        </>
      ) : (
        <LoadingContainer>{getLoadingAndErrorDisplays()}</LoadingContainer>
      )}
    </Root>
  );
};

export default InsuranceHeadline;
